import { useEffect, useState } from "react";
import { Link2, Lock, Trash2, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section du drawer admin : parcelles verrouillées au nom d'un utilisateur.
 * Permet de détacher une parcelle et d'ouvrir la carte en mode rattachement.
 */
export default function UserParcelsSection({ userId, onOpenMap }) {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${API_BASE}/admin/users/${userId}/parcels`);
        const data = await res.json();
        if (!cancelled) setParcels(data?.parcels || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const remove = async (pid) => {
    setRemoving(pid);
    try {
      const res = await authFetch(`${API_BASE}/admin/parcels/${pid}/lock`, { method: "DELETE" });
      if (res.ok) {
        setParcels((prev) => prev.filter((p) => p.parcel_id !== pid));
        toast.success("Parcelle détachée");
      } else {
        toast.error("Détachement impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
    finally { setRemoving(null); }
  };

  const totalHa = parcels.reduce((s, p) => s + (p.surface_ha || 0), 0);

  return (
    <div className="rounded-xl border border-black/5">
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b flex items-center justify-between" style={{ color: "#7A7A7A", borderColor: "#E5E5E0", background: "#FAFAF8" }}>
        <span>Parcelles rattachées{parcels.length ? ` (${parcels.length})` : ""}</span>
        {parcels.length > 0 && <span style={{ color: "#2D6A4F" }}>{totalHa.toFixed(1)} ha</span>}
      </div>
      <div className="p-3 space-y-2">
        {loading ? (
          <p className="text-[11px] text-center py-2" style={{ color: "#9A948A" }}>Chargement…</p>
        ) : parcels.length === 0 ? (
          <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Aucune parcelle rattachée à ce compte.</p>
        ) : (
          parcels.map((p) => (
            <div key={p.parcel_id} className="flex items-center gap-2">
              <span className="w-6 h-6 rounded grid place-items-center flex-none" style={{ background: "#2D6A4F15", color: "#2D6A4F" }}><Lock className="w-3 h-3" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium truncate" style={{ color: "#1A1A1A" }}>{p.culture_type || p.name || "Parcelle"}</span>
                <span className="block text-[10px] truncate" style={{ color: "#7A7A7A" }}>{p.commune || ""}{p.surface_ha ? ` · ${p.surface_ha} ha` : ""}</span>
              </span>
              <button onClick={() => remove(p.parcel_id)} disabled={removing === p.parcel_id}
                className="p-1 rounded hover:bg-red-50 flex-none" style={{ color: "#EF4444" }} aria-label="Détacher">
                {removing === p.parcel_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))
        )}
        <button onClick={onOpenMap}
          className="w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white" style={{ background: "#2D6A4F" }}
          data-testid="attach-on-map-btn">
          <Link2 className="w-3.5 h-3.5" /> Rattacher sur la carte
        </button>
      </div>
    </div>
  );
}
